import React, { useState, useEffect, useRef } from "react";
import styled from "styled-components";
import { useNavigate, useLocation } from "react-router-dom";
import Modal from "react-modal";
import "bootstrap/dist/css/bootstrap.min.css";
import StripePayment from "../Stripe/StripePayment";
import { useTranslation } from 'react-i18next';
// Theme for button colors
const theme = {
  white: {
    default: '#DFBD69',  
    hover: '#b7954d', 
  },
  grey: {
    default: '#DFBD69', 
    hover: '#b7954d',
  },
};

// Button styling
const Button = styled.button`
  background-color: ${(props) => {
    if (props.disabled) return '#333'; // dark grey for disabled
    if (props.selected) return '#DFBD69'; // gold for selected
    return '#000000'; // black background
  }};

  color: ${(props) => {
    if (props.disabled) return '#666';
    if (props.selected) return '#000'; // black text for selected
    return '#DFBD69'; // gold font
  }};

  border: 1px solid rgba(223,189,105,0.12);
  padding: 10px 14px;
  border-radius: ${(props) => props.radius || '10px'};
  outline: 0;
  // border: 1px solid
  //   ${(props) => (props.theme === "white" ? "#000000" : "#d0d0d0")};
  text-transform: uppercase;
  cursor: pointer;
  box-shadow: 0px 2px 2px rgba(0, 0, 0, 0.2);
  transition: background-color 250ms ease;
  margin: 5px;
  flex: 1;

  &:hover {
    background-color: ${(props) =>
      props.disabled ? '#333' : props.selected ? '#DFBD69' : '#111111'};
    color: ${(props) =>
      props.disabled ? '#666' : props.selected ? '#000' : '#DFBD69'};
  }

  &:disabled {
    cursor: default;
    opacity: 0.7;
    background-color: ${(props) =>
      props.disabledColor
        ? props.disabledColor
        : "#A9A9A9"}; // Use custom disabled color if provided
  }
  ${(props) =>
    props.selected &&
    `
    box-shadow: 0 0 0 2px #b7954d;
    font-weight: bold;
    transform: scale(1.02);
  `}
`;

Button.defaultProps = {
  theme: "grey",
  disabledColor: "#A9A9A9", // Default disabled color
  radius: '10px',
  width: 'auto',
};

const ButtonConfirm = styled.button`
  background-color: ${(props) =>
    props.selected ? "#d3f8d3" : theme[props.theme].default};
  color: ${(props) => (props.theme === "white" ? "#000000" : "#333333")};
  padding: 12px 20px;
  border-radius: ${(props) => props.radius || '10px'};
  outline: 0;
  border: 1px solid rgba(223,189,105,0.12);
  text-transform: uppercase;
  cursor: pointer;
  box-shadow: 0px 2px 2px rgba(0, 0, 0, 0.2);
  transition: background-color 250ms ease;
  margin: 5px;
  width: ${(props) => props.width || 'auto'};
    radius: '10px',
    width: 'auto',
  &:hover {
    background-color: ${(props) =>
      props.selected ? "#a7d8a7" : theme[props.theme].hover};
  }
  &:disabled {
    cursor: default;
    opacity: 0.7;
  }
`;

Button.defaultProps = {
  theme: "grey",
};

const Container = styled.div`
  display: grid;
  grid-template-columns: 1fr 3fr;
  gap: 10px;
  margin: 0 auto;
  padding: 20px;
  box-sizing: border-box;
  min-height: 100vh;
  background-color: #171520;
  color: #fff2d2;
  font-family: 'DMSans', Arial, sans-serif;
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    padding: 10px;
  }
`;

const PanelImage = styled.img`
  width: 100%;
  height: auto;
  border-radius: 8px;
  border: 2px solid rgba(223,189,105,0.3);
  margin-bottom: 10px;
`;

// Panel styling
const Panel = styled.div`
  padding: 20px;
  border-radius: 10px;
  box-sizing: border-box;
  width: 100%;
  margin-bottom: 20px;

  &.leftpanel {
    background-color: rgba(10,3,30,0.35);
    border-right: 1px solid rgba(201,168,75,0.12);

    @media (max-width: 768px) {
      border-right: none;
    }
  }

  &.rightpanel {
    background-color: transparent;

    @media (max-width: 768px) {
      border-right: none;
      border-radius: 0;
    }
  }
`;

// Time slot container styling
const TimeSlotContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 10px;
  margin-top: 20px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

// Styling for each time slot
const TimeSlotButton = styled(Button)`
  flex: 1;
  margin: 5px 0;
  background-color: black;
  color: #DFBD69;
`;

// Modal close button styling
// const CloseButton = styled.button`
//   background-color: #e0e0e0;
//   color: #333;
//   border: none;
//   border-radius: 50%;
//   padding: 10px;
//   cursor: pointer;
//   position: absolute;
//   top: 10px;
//   right: 10px;
//   box-shadow: 0px 2px 4px rgba(0, 0, 0, 0.2);
//   font-size: 16px;
//   line-height: 1;
//   transition: background-color 250ms ease;
//   &:hover {
//     background-color: #d0d0d0;
//   }
// `;
const CloseButton = styled.button`
  position: absolute;
  top: 15px;
  right: 15px;
  background-color: transparent;
  border: 2px solid #DFBD69;
  color: #DFBD69;
  font-size: 24px;
  font-weight: bold;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
  z-index: 10;
  &:hover {
    background-color: #DFBD69;
    color: #212121;
  }
`;

// Function to format time for display in 12-hour format with AM/PM
const formatTime = (hour, minute) => {
  const formattedHour = hour % 12 || 12;
  const period = hour < 12 ? "AM" : "PM";
  return `${formattedHour}:${minute < 10 ? "0" + minute : minute} ${period}`;
};

// Function to convert to 24-hour (military) format for backend submission
const toMilitaryTime = (hour, minute) => {
  return `${hour < 10 ? "0" + hour : hour}:${
    minute < 10 ? "0" + minute : minute
  }`;
};

// Generate time slots: each slot is 15 minutes long, starting every 30 minutes.
// Example: 10:00-10:15, 10:30-10:45, 11:00-11:15, ... up to 19:30-19:45
const generateTimeSlots = (startHour = 10, endHour = 20) => {
  const slots = [];
  for (let h = startHour; h < endHour; h++) {
    // two starts per hour: minute 0 and minute 30
    [0, 30].forEach((startMinute) => {
      const start = { hour: h, minute: startMinute };
      // 15 minute duration
      let endHourVal = h;
      let endMinuteVal = startMinute + 15;
      if (endMinuteVal >= 60) {
        endMinuteVal -= 60;
        endHourVal += 1;
      }
      // only include slots where the start is strictly before endHour
      if (h < endHour || (h === endHour && startMinute === 0)) {
        slots.push({ start, end: { hour: endHourVal, minute: endMinuteVal } });
      }
    });
  }
  // Filter out any slot that would start at or after endHour
  return slots.filter(s => s.start.hour < endHour || (s.start.hour === endHour && s.start.minute === 0));
};

const timeSlots = generateTimeSlots(10, 20);

function ThirdPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { selectedMaster, selectedDate } = location.state || {};
  const [selectedTimeSlots, setSelectedTimeSlots] = useState([]);
  const [modalIsOpen, setModalIsOpen] = useState(false);
  const [paymentSuccess, setPaymentSuccess] = useState(false);
  const [paymentFailed, setPaymentFailed] = useState(false);
  const [selectedSlotIndex, setSelectedSlotIndex] = useState(null);
  const [cost, setCost] = useState(null);
  const { t, i18n } = useTranslation();

  const [selectedSlot1, setSelectedSlot1] = useState("");
  const [selectedSlot2, setSelectedSlot2] = useState("");
  const [selectedSlot3, setSelectedSlot3] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [bookingPosted, setBookingPosted] = useState(false);
  // Tracks in-flight submission to prevent duplicate POSTs from rapid clicks
  const submittingRef = useRef(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [clientOrderId] = useState(() => {
    try {
      // lightweight uuid v4 fallback
      return (typeof window !== 'undefined' && window.crypto && window.crypto.randomUUID) ? window.crypto.randomUUID() : 'cid-' + Math.random().toString(36).slice(2, 10);
    } catch (e) {
      return 'cid-' + Math.random().toString(36).slice(2, 10);
    }
  });
  const [date, setDate] = useState("");
  const [isAcknowledged, setIsAcknowledged] = useState(false);
  const [isAcknowledged2, setIsAcknowledged2] = useState(false);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [disabledSlots, setDisabledSlots] = useState([]);
  const ID = selectedMaster?.id || null;
  const [apiData, setApiData] = useState([]);
  const [message, setMessage] = useState("");

  const [validationMessage, setValidationMessage] = useState("");
  const [validatingSlot, setValidatingSlot] = useState(null);
  const [insufficientGemsModal, setInsufficientGemsModal] = useState({ show: false, required: 0, current: 0 });

  // Add state for holiday slots
  const [holidaySlots, setHolidaySlots] = useState([]);

  // Fetch holiday slots and mark them gold
  const fetchHolidaySlots = async (date) => {
    const MasterId = ID;
    const [year, month, day] = date.split('-');
    const currDate = `${month}-${year}`; // Format: MM-YYYY
    try {
      const response = await fetch(`https://xianhefairy.com/LotusFairy_API/api/Booking/GetHolidayDateTime?MasterId=${MasterId}&CurrDate=${currDate}`);
      const data = await response.json();
      // Extract LeaveDate in 'DD-MM-YYYY HH:mm' format and store UID
      setHolidaySlots(data.map(item => ({
        LeaveDate: item.LeaveDate,
        Uid: item.Uid
      })));
    } catch (error) {
      console.error('Error fetching holiday slots:', error);
    }
  };

  // Fetch holiday slots whenever selectedMaster or selectedDate changes
  useEffect(() => {
    if (selectedDate) {
      fetchHolidaySlots(selectedDate);
    }
  }, [selectedMaster, selectedDate]);

  // Helper to check if a slot is a holiday slot
  const isHolidaySlot = (slot) => {
    const slotTime = toMilitaryTime(slot.start.hour, slot.start.minute);
    // Holiday LeaveDate format: 'DD-MM-YYYY HH:mm'
    return holidaySlots.some(hs => {
      const [d, m, yAndTime] = hs.LeaveDate.split('-');
      const [y, time] = yAndTime.split(' ');
      const holidayDate = `${y}-${m.padStart(2, '0')}-${d.padStart(2, '0')}`;
      return holidayDate === selectedDate && time === slotTime;
    });
  };

  // Add validation function
  const validateTimeSlot = async (timeSlot) => {
    const formattedStartTime = toMilitaryTime(
      timeSlot.start.hour,
      timeSlot.start.minute
    );
    
    // Format booking date as YYYY-MM-DD HH:mm
    const bookingDateTime = `${selectedDate} ${formattedStartTime}`;
    
    setValidatingSlot(formattedStartTime);
    
    try {
      const response = await fetch(
        `https://xianhefairy.com/LotusFairy_API/api/Booking/ValidateBooking?CustId=${userID}&bookingDate=${bookingDateTime}`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
        }
      );
      
      if (!response.ok) {
        throw new Error("Validation failed");
      }
      
      const result = await response.json();
      
      // Check if booking is not allowed - the API returns lowercase 'successful'
      if (result.successful === false) {
        const message = result.Message || "You already have an appointment at this time slot. Please select another one";
        setValidationMessage(message);
        alert(message); // Show alert directly here
        setValidatingSlot(null);
        return false;
      }
      
      setValidatingSlot(null);
      return true;
    } catch (error) {
      console.error("Validation error:", error);
      const errorMessage = "Error validating time slot. Please try again.";
      setValidationMessage(errorMessage);
      alert(errorMessage); // Show alert directly here
      setValidatingSlot(null);
      return false;
    }
  };

  // Convert apiData to a more accessible format for disabling time slots.
  // Prefer SchedDate (for scheduled items with time), fall back to REC_DATE.
  const disabledTimeSlots = apiData
    .filter((entry) => entry && (entry.SchedDate || entry.REC_DATE))
    .map((entry) => {
      const dateStr = entry.SchedDate || entry.REC_DATE;
      const date = new Date(dateStr);
      // Format to HH:mm for easier comparison
      const hours = date.getHours().toString().padStart(2, "0");
      const minutes = date.getMinutes().toString().padStart(2, "0");
      return `${hours}:${minutes}`;
    });

  // Debug log so you can see incoming availability data and resulting disabled slots
  console.log("API availability data:", apiData);
  console.log("Computed disabled time slots:", disabledTimeSlots);

  const [isModalOpen, setIsModalOpen] = useState(false); // Modal starts closed
  const [isChecked, setIsChecked] = useState(false); // Checkbox starts unchecked

  const handleCheckboxChange = (event) => {
    setIsChecked(event.target.checked);
  };

  const [guardianModal, setGuardianModal] = useState(false);
  const userAgeRef = useRef(undefined);

  const fetchUserAge = async () => {
    if (userAgeRef.current !== undefined) return userAgeRef.current;
    try {
      const res = await fetch("https://xianhefairy.com/LotusFairy_API/api/User/GetProfile", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          UserName: localStorage.getItem("username") || "",
          ID: parseInt(localStorage.getItem("userID") || "0", 10),
          LangId: parseInt(localStorage.getItem("languageId") || "2224", 10) || 2224,
          UserToken: "",
          Token: { AccessToken: "xAfhn90xflfHfd.fjldfj@fdhLXHCHchlhsdflsdfhawWFRwzxxp" },
        }),
      });
      if (!res.ok) { userAgeRef.current = null; return null; }
      const data = await res.json();
      const age = data?.Profiles?.[0]?.Age ?? null;
      userAgeRef.current = age;
      return age;
    } catch {
      userAgeRef.current = null;
      return null;
    }
  };

  const handleOpenModal = async () => {
    const age = await fetchUserAge();
    if (age !== null && age < 18) {
      setGuardianModal(true);
    } else {
      setModalIsOpen(false); // close receipt modal so Stripe can render on top
      setIsModalOpen(true);
    }
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setModalIsOpen(true); // re-open receipt modal if user cancels Stripe
  };

  // Re-fetch availability whenever selectedMaster or selectedDate change
  useEffect(() => {
    if (!selectedMaster || !selectedDate) return;
    fetchApi();
    console.log("Fetching availability for:", selectedMaster, selectedDate);
  }, [selectedMaster, selectedDate]);

  const fetchApi = async () => {
    try {
      const response = await fetch(
        `https://xianhefairy.com/LotusFairy_API/api/Booking/getBRE?ID=${ID}&Date=${selectedDate}`
      );
      if (!response.ok) {
        throw new Error("Network response was not ok");
      }
      const data = await response.json();
      setApiData(data);
    } catch (error) {
      console.log(error);
    }
  };

  const handleTimeSlotClick = async (timeSlot) => {
    const formattedStartTime = toMilitaryTime(
      timeSlot.start.hour,
      timeSlot.start.minute
    );

    // Deselect if the clicked slot is already selected
    if (selectedSlot1 === formattedStartTime) {
      setSelectedSlot1(null);
      return;
    } else if (selectedSlot2 === formattedStartTime) {
      setSelectedSlot2(null);
      return;
    } else if (selectedSlot3 === formattedStartTime) {
      setSelectedSlot3(null);
      return;
    }
    
    // Validate before selecting
    const isValid = await validateTimeSlot(timeSlot);
    
    if (!isValid) {
      // Don't use validationMessage state here, get it directly from validateTimeSlot
      return;
    }
    
    // Clear validation message if slot is valid
    setValidationMessage("");
    
    // Select slot if no deselection happens
    if (!selectedSlot1) {
      setSelectedSlot1(formattedStartTime);
    } else if (!selectedSlot2 && formattedStartTime !== selectedSlot1) {
      setSelectedSlot2(formattedStartTime);
    } else if (
      !selectedSlot3 &&
      formattedStartTime !== selectedSlot1 &&
      formattedStartTime !== selectedSlot2
    ) {
      setSelectedSlot3(formattedStartTime);
    }
  };

  const isDisabled = (slot) => {
    const slotTime = toMilitaryTime(slot.start.hour, slot.start.minute).slice(0,5); // HH:mm
    // Already taken (from API)
    const taken = disabledTimeSlots.includes(slotTime);

    // Past time logic: if selected date is today and slot start time already passed OR the whole date is in the past
    let isPast = false;
    if (selectedDate) {
      try {
        // Build a Date object for the slot's start datetime (assumes selectedDate is in YYYY-MM-DD or ISO format)
        const slotDateTime = new Date(`${selectedDate}T${slotTime}:00`);
        const now = new Date();
        // If the selected date is before today, mark as past
        const selectedDayStart = new Date(`${selectedDate}T00:00:00`);
        if (selectedDayStart < new Date(now.getFullYear(), now.getMonth(), now.getDate())) {
          isPast = true; // whole day is in the past
        } else if (slotDateTime < now) {
          isPast = true; // time already passed today
        }
      } catch (e) {
        // Fail-safe: do not block if parsing fails
        isPast = false }
    }

    return taken || isPast;
  };

  const handleConfirm = async () => {
    // Fetch latest gem count from API
    await fetchData();
    
    // ✅ Validate total coins before showing modal
    const totalGemCost = calculateGemCost();
    const totalCardCost = calculateTotalCost();
    
    console.log('Total Gem cost required:', totalGemCost);
    console.log('Total Card cost required:', totalCardCost);
    console.log('Current coins/gems:', currentCoins);
    console.log(selectedSlot1);
    console.log(selectedSlot2);
    console.log(selectedSlot3);
    
    // Record gem numbers so the receipt modal can display them.
    // Do NOT show a blocking alert here; instead show the receipt modal
    // and let the user decide whether to Pay by Gems or Card.
    if (totalGemCost > 0) {
      setInsufficientGemsModal({ show: false, required: totalGemCost, current: currentCoins });
    } else {
      setInsufficientGemsModal({ show: false, required: 0, current: currentCoins });
    }

    setModalIsOpen(true);
  };

  const handleCloseModal = () => {
    setModalIsOpen(false);
  };

  // const calculateTotalCost = () => {
  //   const masterCost = selectedMaster.name === "Master Siewli" ? 200 : 150;
  //   const dateCost = [0, 6].includes(new Date(selectedDate).getDay())
  //     ? 100
  //     : 50;
  //   const timeSlotCost = selectedTimeSlotsArray.length * 200;

  //   return masterCost + dateCost + timeSlotCost;
  // };

  const calculateTotalCost = () => {
    // Use master's rate as provided (0 is valid). Default to 0 if missing.
    const rate = Number(selectedMaster?.rate ?? 0);
    return (Number.isFinite(rate) ? rate : 0) * selectedTimeSlotsArray.length;
  };

  const calculateGemCost = () => {
    // Use master's gem_rate directly
    const gemRate = Number(selectedMaster?.gem_rate ?? 0);
    return (Number.isFinite(gemRate) ? gemRate : 0) * selectedTimeSlotsArray.length;
  };
  const userID = localStorage.getItem("userID");
  const startDateTime = `${selectedDate} ${selectedTimeSlots.start}`;

  const checkLoginAndOrder = () => {
    const isLoggedIn = localStorage.getItem("isLoggedIn");
    if (isLoggedIn === "true") {
      console.log(selectedMaster);
      navigate("/schedule2", {
        state: { selectedMaster },
      });
    } else {
      navigate("/login", {
        state: { message: "You need to log in before placing an order." },
      });
    }
  };

  const [currentCoins, setCurrentCoins] = useState(0);
  const fetchData = async () => {
    try {
      // Make the GET request
      const response = await fetch(
        `https://xianhefairy.com/LotusFairy_API/api/booking/getTotal?ID=${userID}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      // Check if the response is OK
      if (!response.ok) {
        throw new Error("Failed to fetch data");
      }

      // Parse the JSON response
      const data = await response.json();
      localStorage.setItem("coins", data[0].COINS);
      setCurrentCoins(data[0].COINS);
    } catch (error) {
      console.error("Error fetching data:", error);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);


  // const handleConfirmPayment = async () => {
  //   const totalCost = calculateTotalCost();
  //   // Defensive: ensure cost is computed
  //   if (!totalCost || isNaN(totalCost)) {
  //     alert("Unable to determine total cost. Please try again.");
  //     return;
  //   }

  //   if (Number(currentCoins) <= 0) {
  //     alert("You have 0 charms. Please top up before purchasing.");
  //     return;
  //   }

  //   if (totalCost > Number(currentCoins)) {
  //     alert("You dont have enough charms");
  //     return;
  //   }

  //   // Build PROD_ITEM array in the requested shape
  //   const prodItems = [];

  //   const masterAmount = selectedMaster?.name === "Master Siewli" ? 200 : 150;
  //   const dateAmount = [0, 6].includes(new Date(selectedDate).getDay()) ? 100 : 50;

  //   // Master line (TYPE:0)
  //   prodItems.push({
  //     FSMasterID: selectedMaster.id,
  //     CustomerId: Number(userID) || 0,
  //     pid: selectedMaster?.id || 0,
  //     Quantity: 1,
  //     Amount: masterAmount,
  //     DESC_ENG: `Master: ${selectedMaster?.name}`,
  //     ReserveStatus: 0,
  //     TYPE: 0,
  //   });

  //   // Date line (TYPE:0)
  //   prodItems.push({
  //     FSMasterID: selectedMaster.id,
  //     CustomerId: Number(userID) || 0,
  //     pid: selectedMaster?.id || 0,
  //     Quantity: 1,
  //     Amount: dateAmount,
  //     DESC_ENG: `Date: ${new Date(selectedDate).toLocaleDateString()}`,
  //     ReserveStatus: 0,
  //     TYPE: 0,
  //   });

  //   // Time slot lines (one per selected slot) (TYPE:1)
  //   if (selectedSlot1) {
  //     const sched = `${selectedDate}T${selectedSlot1}:00`;
  //     prodItems.push({
  //       FSMasterID: selectedMaster.id,
  //       CustomerId: Number(userID) || 0,
  //       pid: selectedMaster?.id || 0,
  //       Quantity: 1,
  //       Amount: 200,
  //       DESC_ENG: `Booking Schedule: ${sched}`,
  //       SchedDate: sched,
  //       ReserveStatus: 0,
  //       TYPE: 1,
  //     });
  //   }
  //   if (selectedSlot2) {
  //     const sched = `${selectedDate}T${selectedSlot2}:00`;
  //     prodItems.push({
  //       FSMasterID: selectedMaster.id,
  //       CustomerId: Number(userID) || 0,
  //       pid: selectedMaster?.id || 0,
  //       Quantity: 1,
  //       Amount: 200,
  //       DESC_ENG: `Booking Schedule: ${sched}`,
  //       SchedDate: sched,
  //       ReserveStatus: 0,
  //       TYPE: 1,
  //     });
  //   }
  //   if (selectedSlot3) {
  //     const sched = `${selectedDate}T${selectedSlot3}:00`;
  //     prodItems.push({
  //       FSMasterID: selectedMaster.id,
  //       CustomerId: Number(userID) || 0,
  //       pid: selectedMaster?.id || 0,
  //       Quantity: 1,
  //       Amount: 200,
  //       DESC_ENG: `Booking Schedule: ${sched}`,
  //       SchedDate: sched,
  //       ReserveStatus: 0,
  //       TYPE: 1,
  //     });
  //   }

  //   if (prodItems.length === 0) {
  //     setError("Please select at least one time slot.");
  //     return;
  //   }

  //   // Check for duplicate schedules (same HH:mm already booked)
  //   const selectedSlots = [selectedSlot1, selectedSlot2, selectedSlot3].filter(Boolean);
  //   const duplicateSlots = selectedSlots.filter((s) => {
  //     const hhmm = s.slice(0,5);
  //     return disabledTimeSlots.includes(hhmm);
  //   });
  //   if (duplicateSlots.length > 0) {
  //     const msgs = duplicateSlots.map(d => `${d} already booked`).join(", ");
  //     setError(`Cannot book: ${msgs}`);
  //     alert(`Cannot book these time(s): ${msgs}`);
  //     return;
  //   }

  //   // Top-level envelope matching requested format
  //   const envelope = {
  //     PREFIX: "PB",
  //     PAY_MODE: "0", // 0 = Gems
  //     UPDATE_BY: localStorage.getItem("username") || "",
  //     PROD_ITEM: prodItems,
  //   };

  //   setLoading(true);
  //   setError(null);
  // // Log booking envelope for debugging
  // // Attach a clientOrderId to help server-side dedupe if available
  // envelope.CLIENT_ORDER_ID = clientOrderId;
  // console.log("Booking envelope (Gems):", JSON.stringify(envelope, null, 2));
  // if (bookingPosted) {
  //   console.warn('Booking already posted from this client instance; skipping duplicate POST.');
  //   return;
  // }

  //   try {
  //     const res = await fetch(
  //       "https://xianhefairy.com/LotusFairy_API/api/member/PostBookingSchedule",
  //       {
  //         method: "POST",
  //         headers: { "Content-Type": "application/json" },
  //         body: JSON.stringify(envelope),
  //       }
  //     );

  //     if (!res.ok) {
  //       const errorData = await res.json();
  //       console.error("Error response:", errorData);
  //       throw new Error(`HTTP error! status: ${res.status}`);
  //     }

  //     const result = await res.json();
  //     // Mark posted so UI won't post again
  //     setBookingPosted(true);
  //     setPaymentSuccess(true);
  //     setMessage(result.Msg || "Booking successful");
  //     // Update apiData so disabledTimeSlots reflects new booking immediately
  //     const newApiEntries = selectedSlots.map(s => ({ SchedDate: `${selectedDate}T${s}:00` }));
  //     setApiData(prev => [...prev, ...newApiEntries]);
  //   } catch (err) {
  //     setError(err.message);
  //     console.error("Request failed:", err);
  //     setPaymentFailed(true);
  //   } finally {
  //     setLoading(false);
  //   }
  // };

  const handleConfirmPayment = async () => {
    // Guard against double clicks while submitting
    if (isSubmitting || submittingRef.current || bookingPosted) {
      return;
    }
    // Fetch latest gem count from API
    await fetchData();
    
    const totalCost = calculateGemCost();
    // Defensive: ensure cost is computed
    if (!totalCost || isNaN(totalCost)) {
      alert("Unable to determine total cost. Please try again.");
      return;
    }

    if (Number(currentCoins) <= 0) {
      setInsufficientGemsModal({ show: true, required: totalCost, current: currentCoins });
      return;
    }

    if (totalCost > Number(currentCoins)) {
      setInsufficientGemsModal({ show: true, required: totalCost, current: currentCoins });
      return;
    }

    // Build PROD_ITEM array in the requested shape
    const prodItems = [];

    // Master line (TYPE:0)
    prodItems.push({
      FSMasterID: selectedMaster.id,
      CustomerId: Number(userID) || 0,
      pid: selectedMaster?.id || 0,
      Quantity: 1,
      Amount: totalCost, // Gems price
      DESC_ENG: `Master: ${selectedMaster?.name}`,
      ReserveStatus: 0,
      TYPE: 0,
    });

    // Date line (TYPE:0)
    // prodItems.push({
    //   FSMasterID: selectedMaster.id,
    //   CustomerId: Number(userID) || 0,
    //   pid: selectedMaster?.id || 0,
    //   Quantity: 1,
    //   Amount: dateAmount,
    //   DESC_ENG: `Date: ${new Date(selectedDate).toLocaleDateString()}`,
    //   ReserveStatus: 0,
    //   TYPE: 0,
    // });

    // Time slot lines (one per selected slot) (TYPE:1)
    if (selectedSlot1) {
      const sched = `${selectedDate}T${selectedSlot1}:00`;
      prodItems.push({
        FSMasterID: selectedMaster.id,
        CustomerId: Number(userID) || 0,
        pid: selectedMaster?.id || 0,
        Quantity: 1,
        Amount: 0,
        DESC_ENG: `Booking Schedule: ${sched}`,
        SchedDate: sched,
        ReserveStatus: 0,
        TYPE: 1,
      });
    }
    if (selectedSlot2) {
      const sched = `${selectedDate}T${selectedSlot2}:00`;
      prodItems.push({
        FSMasterID: selectedMaster.id,
        CustomerId: Number(userID) || 0,
        pid: selectedMaster?.id || 0,
        Quantity: 1,
        Amount: 0,
        DESC_ENG: `Booking Schedule: ${sched}`,
        SchedDate: sched,
        ReserveStatus: 0,
        TYPE: 1,
      });
    }
    if (selectedSlot3) {
      const sched = `${selectedDate}T${selectedSlot3}:00`;
      prodItems.push({
        FSMasterID: selectedMaster.id,
        CustomerId: Number(userID) || 0,
        pid: selectedMaster?.id || 0,
        Quantity: 1,
        Amount: 0,
        DESC_ENG: `Booking Schedule: ${sched}`,
        SchedDate: sched,
        ReserveStatus: 0,
        TYPE: 1,
      });
    }

    if (prodItems.length === 0) {
      setError("Please select at least one time slot.");
      return;
    }

    // Check for duplicate schedules (same HH:mm already booked)
    const selectedSlots = [selectedSlot1, selectedSlot2, selectedSlot3].filter(Boolean);
    const duplicateSlots = selectedSlots.filter((s) => {
      const hhmm = s.slice(0,5);
      return disabledTimeSlots.includes(hhmm);
    });
    if (duplicateSlots.length > 0) {
      const msgs = duplicateSlots.map(d => `${d} already booked`).join(", ");
      setError(`Cannot book: ${msgs}`);
      alert(`Cannot book these time(s): ${msgs}`);
      return;
    }

    // Top-level envelope matching requested format
    const envelope = {
      PREFIX: "PB",
      PAY_MODE: "0", // 0 = Gems
      UPDATE_BY: localStorage.getItem("username") || "",
      PROD_ITEM: prodItems,
    };

    setLoading(true);
    setError(null);
    setIsSubmitting(true);
    submittingRef.current = true;
  // Log booking envelope for debugging
  // Attach a clientOrderId to help server-side dedupe if available
  envelope.CLIENT_ORDER_ID = clientOrderId;
  console.log("Booking envelope (Gems):", JSON.stringify(envelope, null, 2));
  if (bookingPosted) {
    console.warn('Booking already posted from this client instance; skipping duplicate POST.');
    setIsSubmitting(false);
    submittingRef.current = false;
    return;
  }

    try {
      const res = await fetch(
        "https://xianhefairy.com/LotusFairy_API/api/member/PostBookingSchedule",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(envelope),
        }
      );

      if (!res.ok) {
        const errorData = await res.json();
        console.error("Error response:", errorData);
        throw new Error(`HTTP error! status: ${res.status}`);
      }

      const result = await res.json();
      // Mark posted so UI won't post again
      setBookingPosted(true);
      setPaymentSuccess(true);
      setMessage(result.Msg || "Booking successful");
      // Update apiData so disabledTimeSlots reflects new booking immediately
      const newApiEntries = selectedSlots.map(s => ({ SchedDate: `${selectedDate}T${s}:00` }));
      setApiData(prev => [...prev, ...newApiEntries]);
    } catch (err) {
      setError(err.message);
      console.error("Request failed:", err);
      setPaymentFailed(true);
    } finally {
      setLoading(false);
      setIsSubmitting(false);
      submittingRef.current = false;
    }
  };

  // const handlePaymentbyCard = async () => {
  //   // Build PROD_ITEM array in the requested shape
  //   const prodItems = [];

  //   const masterAmount = selectedMaster?.name === "Master Siewli" ? 200 : 150;
  //   const dateAmount = [0, 6].includes(new Date(selectedDate).getDay()) ? 100 : 50;

  //   // Master line (TYPE:0)
  //   prodItems.push({
  //     FSMasterID: selectedMaster.id,
  //     CustomerId: Number(userID) || 0,
  //     pid: selectedMaster?.id || 0,
  //     Quantity: 1,
  //     Amount: masterAmount,
  //     DESC_ENG: `Master: ${selectedMaster?.name}`,
  //     ReserveStatus: 0,
  //     TYPE: 0,
  //   });

  //   // Date line (TYPE:0)
  //   prodItems.push({
  //     FSMasterID: selectedMaster.id,
  //     CustomerId: Number(userID) || 0,
  //     pid: selectedMaster?.id || 0,
  //     Quantity: 1,
  //     Amount: dateAmount,
  //     DESC_ENG: `Date: ${new Date(selectedDate).toLocaleDateString()}`,
  //     ReserveStatus: 0,
  //     TYPE: 0,
  //   });

  //   // Time slot lines (one per selected slot) (TYPE:1)
  //   if (selectedSlot1) {
  //     const sched = `${selectedDate}T${selectedSlot1}:00`;
  //     prodItems.push({
  //       FSMasterID: selectedMaster.id,
  //       CustomerId: Number(userID) || 0,
  //       pid: selectedMaster?.id || 0,
  //       Quantity: 1,
  //       Amount: 200,
  //       DESC_ENG: `Booking Schedule: ${sched}`,
  //       SchedDate: sched,
  //       ReserveStatus: 0,
  //       TYPE: 1,
  //     });
  //   }
  //   if (selectedSlot2) {
  //     const sched = `${selectedDate}T${selectedSlot2}:00`;
  //     prodItems.push({
  //       FSMasterID: selectedMaster.id,
  //       CustomerId: Number(userID) || 0,
  //       pid: selectedMaster?.id || 0,
  //       Quantity: 1,
  //       Amount: 200,
  //       DESC_ENG: `Booking Schedule: ${sched}`,
  //       SchedDate: sched,
  //       ReserveStatus: 0,
  //       TYPE: 1,
  //     });
  //   }
  //   if (selectedSlot3) {
  //     const sched = `${selectedDate}T${selectedSlot3}:00`;
  //     prodItems.push({
  //       FSMasterID: selectedMaster.id,
  //       CustomerId: Number(userID) || 0,
  //       pid: selectedMaster?.id || 0,
  //       Quantity: 1,
  //       Amount: 200,
  //       DESC_ENG: `Booking Schedule: ${sched}`,
  //       SchedDate: sched,
  //       ReserveStatus: 0,
  //       TYPE: 1,
  //     });
  //   }

  //   if (prodItems.length === 0) {
  //     setError("Please select at least one time slot.");
  //     return;
  //   }

  //   // Check for duplicate schedules (same HH:mm already booked)
  //   const selectedSlots = [selectedSlot1, selectedSlot2, selectedSlot3].filter(Boolean);
  //   const duplicateSlots = selectedSlots.filter((s) => {
  //     const hhmm = s.slice(0,5);
  //     return disabledTimeSlots.includes(hhmm);
  //   });
  //   if (duplicateSlots.length > 0) {
  //     const msgs = duplicateSlots.map(d => `${d} already booked`).join(", ");
  //     setError(`Cannot book: ${msgs}`);
  //     alert(`Cannot book these time(s): ${msgs}`);
  //     return;
  //   }

  //   // Top-level envelope matching requested format
  //   const envelope = {
  //     PREFIX: "PB",
  //     PAY_MODE: "1", // 1 = Card
  //     UPDATE_BY: localStorage.getItem("username") || "",
  //     PROD_ITEM: prodItems,
  //   };

  //   setLoading(true);
  //   setError(null);
  // // Log booking envelope for debugging
  // envelope.CLIENT_ORDER_ID = clientOrderId;
  // console.log("Booking envelope (Card):", JSON.stringify(envelope, null, 2));
  // if (bookingPosted) {
  //   console.warn('Booking already posted from this client instance; skipping duplicate POST.');
  //   return;
  // }

  //   try {
  //     const res = await fetch(
  //       "https://xianhefairy.com/LotusFairy_API/api/member/PostBookingSchedule",
  //       {
  //         method: "POST",
  //         headers: { "Content-Type": "application/json" },
  //         body: JSON.stringify(envelope),
  //       }
  //     );

  //     if (!res.ok) {
  //       const errorData = await res.json();
  //       console.error("Error response:", errorData);
  //       throw new Error(`HTTP error! status: ${res.status}`);
  //     }

  //   const result = await res.json();
  //   // Mark posted so UI won't post again
  //   setBookingPosted(true);
  //   setPaymentSuccess(true);
  //   setMessage(result.Msg || "Booking successful");
  //   // Update apiData so disabledTimeSlots reflects new booking immediately
  //   const newApiEntries = selectedSlots.map(s => ({ SchedDate: `${selectedDate}T${s}:00` }));
  //   setApiData(prev => [...prev, ...newApiEntries]);
  //   handleCloseModal();
  //   } catch (err) {
  //     setError(err.message);
  //     console.error("Request failed:", err);
  //     setPaymentFailed(true);
  //   } finally {
  //     setLoading(false);
  //   }
  // };
  const handlePaymentbyCard = async () => {
    // Guard against duplicate submissions
    if (isSubmitting || submittingRef.current || bookingPosted) {
      return;
    }
  // Card payment uses master's rate (0 is valid). Default to 0 if missing.
  const totalCost = Number(selectedMaster?.rate ?? 0);

    // Build PROD_ITEM array in the requested shape
    const prodItems = [];

    // Master line (TYPE:0)
    prodItems.push({
      FSMasterID: selectedMaster.id,
      CustomerId: Number(userID) || 0,
      pid: selectedMaster?.id || 0,
      Quantity: 1,
      Amount: totalCost,
      DESC_ENG: `Master: ${selectedMaster?.name}`,
      ReserveStatus: 0,
      TYPE: 0,
    });

    // Date line (TYPE:0)
    // prodItems.push({
    //   FSMasterID: selectedMaster.id,
    //   CustomerId: Number(userID) || 0,
    //   pid: selectedMaster?.id || 0,
    //   Quantity: 1,
    //   Amount: dateAmount,
    //   DESC_ENG: `Date: ${new Date(selectedDate).toLocaleDateString()}`,
    //   ReserveStatus: 0,
    //   TYPE: 0,
    // });

    // Time slot lines (one per selected slot) (TYPE:1)
    if (selectedSlot1) {
      const sched = `${selectedDate}T${selectedSlot1}:00`;
      prodItems.push({
        FSMasterID: selectedMaster.id,
        CustomerId: Number(userID) || 0,
        pid: selectedMaster?.id || 0,
        Quantity: 1,
        Amount: 0,
        DESC_ENG: `Booking Schedule: ${sched}`,
        SchedDate: sched,
        ReserveStatus: 0,
        TYPE: 1,
      });
    }
    if (selectedSlot2) {
      const sched = `${selectedDate}T${selectedSlot2}:00`;
      prodItems.push({
        FSMasterID: selectedMaster.id,
        CustomerId: Number(userID) || 0,
        pid: selectedMaster?.id || 0,
        Quantity: 1,
        Amount: 0,
        DESC_ENG: `Booking Schedule: ${sched}`,
        SchedDate: sched,
        ReserveStatus: 0,
        TYPE: 1,
      });
    }
    if (selectedSlot3) {
      const sched = `${selectedDate}T${selectedSlot3}:00`;
      prodItems.push({
        FSMasterID: selectedMaster.id,
        CustomerId: Number(userID) || 0,
        pid: selectedMaster?.id || 0,
        Quantity: 1,
        Amount: 0,
        DESC_ENG: `Booking Schedule: ${sched}`,
        SchedDate: sched,
        ReserveStatus: 0,
        TYPE: 1,
      });
    }

    if (prodItems.length === 0) {
      setError("Please select at least one time slot.");
      return;
    }

    // Check for duplicate schedules (same HH:mm already booked)
    const selectedSlots = [selectedSlot1, selectedSlot2, selectedSlot3].filter(Boolean);
    const duplicateSlots = selectedSlots.filter((s) => {
      const hhmm = s.slice(0,5);
      return disabledTimeSlots.includes(hhmm);
    });
    if (duplicateSlots.length > 0) {
      const msgs = duplicateSlots.map(d => `${d} already booked`).join(", ");
      setError(`Cannot book: ${msgs}`);
      alert(`Cannot book these time(s): ${msgs}`);
      return;
    }

    // Top-level envelope matching requested format
    const envelope = {
      PREFIX: "PB",
      PAY_MODE: "1", // 1 = Card
      UPDATE_BY: localStorage.getItem("username") || "",
      PROD_ITEM: prodItems,
    };

    setLoading(true);
    setError(null);
    setIsSubmitting(true);
    submittingRef.current = true;
  // Log booking envelope for debugging
  envelope.CLIENT_ORDER_ID = clientOrderId;
  console.log("Booking envelope (Card):", JSON.stringify(envelope, null, 2));
  if (bookingPosted) {
    console.warn('Booking already posted from this client instance; skipping duplicate POST.');
    setIsSubmitting(false);
    submittingRef.current = false;
    return;
  }

    try {
      const res = await fetch(
        "https://xianhefairy.com/LotusFairy_API/api/member/PostBookingSchedule",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(envelope),
        }
      );

      if (!res.ok) {
        const errorData = await res.json();
        console.error("Error response:", errorData);
        throw new Error(`HTTP error! status: ${res.status}`);
      }

    const result = await res.json();
    // Mark posted so UI won't post again
    setBookingPosted(true);
    setPaymentSuccess(true);
    setMessage(result.Msg || "Booking successful");
    // Update apiData so disabledTimeSlots reflects new booking immediately
    const newApiEntries = selectedSlots.map(s => ({ SchedDate: `${selectedDate}T${s}:00` }));
    setApiData(prev => [...prev, ...newApiEntries]);
    setIsModalOpen(false); // close Stripe modal
    handleCloseModal();    // close receipt modal
    } catch (err) {
      setError(err.message);
      console.error("Request failed:", err);
      setPaymentFailed(true);
    } finally {
      setLoading(false);
      setIsSubmitting(false);
      submittingRef.current = false;
    }
  };

  // const handleConfirmPayment = () => {
  //   const totalCost = calculateTotalCost();
  //   const currentCoins = parseInt(localStorage.getItem('coins') || '0', 10);

  //   if (currentCoins >= totalCost) {
  //     localStorage.setItem('coins', currentCoins - totalCost);
  //     setPaymentSuccess(true);
  //   } else {
  //     setPaymentFailed(true);
  //   }

  //   handleCloseModal();
  //   console.log()
  // };

  const closeSuccessModal = () => {
    setModalIsOpen(false);
    setPaymentSuccess(false);
    window.location.reload();
  };

  const closeFailedModal = () => setPaymentFailed(false);

  const goBack = () => {
    navigate(-1); // This navigates to the previous page
  };

  const selectedTimeSlotsArray = [
    selectedSlot1,
    selectedSlot2,
    selectedSlot3,
  ].filter((slot) => slot);

  return (
    <Container>
      <Panel className="leftpanel">
        {selectedMaster && (
          <>
            <PanelImage src={selectedMaster.image} alt="Master" />
            <h2 style={{ color: "#DFBD69" }}>{selectedMaster.name}</h2>
            <p style={{ color: "#DFBD69" }}>{selectedMaster.description}</p>
          </>
        )}
      </Panel>
      <Panel className="rightpanel">
        <div
          style={{
            display: "flex",
            justifyContent: "flex-end", // Aligns the button to the right of the container
            padding: "20px",
          }}
        >
        </div>
        <h4 style={{ color: "#DFBD69" }}>{t('appointmentsPage.selectTimeSlots') || 'Select Time Slots - 15 minutes'}</h4>
        <TimeSlotContainer>
          {timeSlots.map((slot, index) => {
            // Format the start and end times to display in 12-hour format
            const formattedStartTime = formatTime(
              slot.start.hour,
              slot.start.minute
            );
            const formattedEndTime = formatTime(slot.end.hour, slot.end.minute);

            const isHoliday = isHolidaySlot(slot);

            return (
              <TimeSlotButton
                key={index}
                selected={
                  selectedSlot1 ===
                    toMilitaryTime(slot.start.hour, slot.start.minute) ||
                  selectedSlot2 ===
                    toMilitaryTime(slot.start.hour, slot.start.minute) ||
                  selectedSlot3 ===
                    toMilitaryTime(slot.start.hour, slot.start.minute)
                }
                onClick={() => 
                  !isDisabled(slot) && !isHoliday && handleTimeSlotClick(slot)
                }
                disabled={
                  isDisabled(slot) ||
                  validatingSlot === toMilitaryTime(slot.start.hour, slot.start.minute) ||
                  isHoliday // Disable if holiday
                }
                disabledColor="#A9A9A9"
                style={isHoliday ? { backgroundColor: '#DFBD69', color: '#000', fontWeight: 'bold' } : {}}
              >
                {validatingSlot === toMilitaryTime(slot.start.hour, slot.start.minute)
                  ? "Validating..."
                  : formattedStartTime}
                {isHoliday && <span style={{ marginLeft: 6, color: '#000', fontWeight: 'bold' }}>(On Leave)</span>}
              </TimeSlotButton>
            );
          })}
        </TimeSlotContainer>

        {/* Align Go Back and Confirm horizontally */}
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginTop: "20px",
            width: "100%",
          }}
        >
          <button
            onClick={goBack}
            style={{
              padding: "10px 20px",
              backgroundColor: "#DFBD69",
              color: "black",
              border: "1px solid grey",
              borderRadius: "5px",
              cursor: "pointer",
              fontSize: "16px",
            }}
          >
            {t('appointmentsPage.goBack')}
          </button>
          <ButtonConfirm onClick={handleConfirm} theme="white">
            {t('appointmentsPage.confirmTimeSlots')}
          </ButtonConfirm>
        </div>
      </Panel>

      <Modal
        isOpen={modalIsOpen}
        onRequestClose={handleCloseModal}
        style={{
          content: {
            width: "80%",
            height: "50%",
            margin: "auto",
            padding: "20px",
            maxWidth: "100%",
            borderRadius: "15px",
            backgroundColor: "black",
            zIndex: 500,
          },
          overlay: {
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            zIndex: 1000,
          },
        }}
      >
        <CloseButton onClick={handleCloseModal}>×</CloseButton>
        <h2 style={{ color: "#DFBD69" }}>{t('appointmentsPage.receipt')}</h2>

        {/* Quick gem summary for clarity */}
        <div style={{
          backgroundColor: '#0f0f0f',
          padding: '12px',
          borderRadius: '8px',
          border: '1px solid rgba(223,189,105,0.12)',
          marginBottom: '12px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <div style={{ color: '#DFBD69' }}>
            <div><strong>Required Gems:</strong> {calculateGemCost()}</div>
            <div><strong>Your Gems:</strong> {currentCoins ?? 0}</div>
          </div>
          {(Number(currentCoins) || 0) < (Number(calculateGemCost()) || 0) && (
            <div style={{ color: '#ef4444', fontWeight: 'bold' }}>
              Insufficient Gems
            </div>
          )}
        </div>

        {/* Costs Layout */}
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            marginBottom: "10px",
            flexWrap: "wrap",
          }}
        >
          <p style={{ flex: "1", color: "#DFBD69" }}>
            <strong>{t('appointmentsPage.master')}:</strong> {selectedMaster.name}
            <br />
            <small style={{ color: '#DFBD69' }}>
              {t('appointmentsPage.rateperslot')}: {Number(selectedMaster?.gem_rate ?? 0)} {t('appointmentsPage.gem')}
            </small>
          </p>
          <p style={{ flex: "1", textAlign: "right", color: "#DFBD69" }}>
            <span style={{ color: "#DFBD69", fontWeight: "bold", fontSize: "1.2em", marginTop: "5px" }}>
              US$ {calculateTotalCost()}
            </span>
          </p>
        </div>

        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            fontWeight: "bold",
            fontSize: "1.2em",
            marginTop: "10px",
            color: "#DFBD69",
          }}
        >
          <p>{t('appointmentsPage.totalCost')}:</p>
          <p style={{ marginBottom: 0 }}>
            {t('appointmentsPage.gem')}: {calculateGemCost()} 
            &nbsp;|&nbsp; 
            Card: US$ {calculateTotalCost()}
          </p>
        </div>

        {/* Checkbox for acknowledgment */}
        <div style={{ marginTop: "20px" }}>
          <label style={{ color: "#DFBD69"}}>
            <input
              type="checkbox"
              checked={isAcknowledged}
              onChange={(e) => setIsAcknowledged(e.target.checked)}
            />
            {t('appointmentsPage.acknowledgement') || 'I acknowledge the terms and conditions of the consultation.'}
          </label>
        </div>

        <div style={{ marginTop: "20px" }}>
          <label style={{ color: "#DFBD69" }}>
            <input
              type="checkbox"
              checked={isAcknowledged2}
              onChange={(e) => setIsAcknowledged2(e.target.checked)}
            />
            {t('appointmentsPage.acknowledgement2') || 'I acknowledge the consultation is 15 minutes only and will be on time'}
          </label>
        </div>

        {/* Buttons */}
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            marginTop: "20px",
            flexWrap: "wrap",
          }}
        >
          <Button
            onClick={handleCloseModal}
            theme="white"
            style={{ flex: "1", marginRight: "10px" }}
          >
            Close
          </Button>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, flex: 1 }}>
            <div style={{ color: '#DFBD69', fontWeight: 600 }}>Gems: {currentCoins ?? 0}</div>
            <Button
              onClick={handleConfirmPayment}
              theme="grey"
              style={{ flex: "1" }}
              disabled={!isAcknowledged || !isAcknowledged2 || Number(currentCoins) <= 0 || isSubmitting}
            >
              {isSubmitting ? t('appointmentsPage.processing') || "Processing..." : t('appointmentsPage.paybygems') || "Pay by Gems"}
            </Button>
          </div>
          <Button
            onClick={handleOpenModal}
            theme="grey"
            style={{ flex: "1" }}
            disabled={!isAcknowledged || !isAcknowledged2 || isSubmitting} // Button disabled if checkbox not checked or submitting
          >
            Pay by Card
          </Button>
        </div>
      </Modal>

      {isModalOpen && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100vw",
            height: "100vh",
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 20000,
          }}
          onClick={closeModal} // Close when clicking outside the modal
        >
          <div
            style={{
              backgroundColor: "#fff",
              padding: "20px",
              borderRadius: "10px",
              boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.1)",
              minWidth: "300px",
            }}
            onClick={(e) => e.stopPropagation()} // Prevent modal close when clicking inside
          >
            <StripePayment
              id="payment by card"
              amount={calculateTotalCost()}
              currency="usd"
              isOpen={isModalOpen}
              onClose={closeModal}
              onPaymentSuccess={handlePaymentbyCard}
            />
          </div>
        </div>
      )}

      {/* Payment Success Modal */}
      <Modal
        isOpen={paymentSuccess}
        onRequestClose={closeSuccessModal}
        style={{
          content: {
            width: "388px",
            height: "35%",
            margin: "auto",
            padding: "20px",
            backgroundColor: "black",
          },
        }}
      >
        <CloseButton onClick={closeSuccessModal}>×</CloseButton>
        <h2 style={{ color: "#DFBD69" }}>{t('appointmentsPage.reservation')}</h2>
        <p style={{ color: "#DFBD69" }}>{t('appointmentsPage.bookingsuccesful')}</p>
        <Button onClick={closeSuccessModal} theme="white">
          {t('appointmentsPage.close') || 'Close'}
        </Button>
      </Modal>

      {/* Payment Failed Modal */}
      <Modal
        isOpen={paymentFailed}
        onRequestClose={closeFailedModal}
        style={{
          content: {
            width: "388px",
            height: "35%",
            margin: "auto",
            padding: "20px",
            backgroundColor: "black",
          },
        }}
      >
        {/* <CloseButton onClick={closeFailedModal}>×</CloseButton> */}
        <h2 style={{ color: "#DFBD69" }}>Payment Failed</h2>
        <p style={{ color: "#DFBD69" }}>{message}</p>
        <Button onClick={closeFailedModal} theme="white">
          Close
        </Button>
      </Modal>
      
      {/* Insufficient Gems Modal */}
      {insufficientGemsModal.show && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100vw',
            height: '100vh',
            backgroundColor: 'rgba(0, 0, 0, 0.7)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 10000
          }}
          onClick={() => setInsufficientGemsModal({ show: false, required: 0, current: 0 })}
        >
          <div
            style={{
              backgroundColor: '#000',
              padding: '30px',
              borderRadius: '15px',
              maxWidth: '500px',
              width: '90%',
              textAlign: 'center',
              border: '2px solid #DFBD69',
              position: 'relative'
            }}
            onClick={e => e.stopPropagation()}
          >
            <CloseButton onClick={() => setInsufficientGemsModal({ show: false, required: 0, current: 0 })}>×</CloseButton>
            <h2 style={{ color: '#ef4444', marginBottom: '20px' }}>Insufficient Gems</h2>
            <p style={{ color: '#DFBD69', marginBottom: '15px', fontSize: '16px' }}>
              You do not have enough Gems for this booking.
            </p>
            <div style={{ 
              backgroundColor: '#1a1a1a', 
              padding: '15px', 
              borderRadius: '8px',
              marginBottom: '20px',
              border: '1px solid #DFBD69'
            }}>
              <p style={{ color: '#DFBD69', marginBottom: '10px' }}>
                <strong>Required:</strong> {insufficientGemsModal.required} Gems
              </p>
              <p style={{ color: '#DFBD69' }}>
                <strong>You have:</strong> {insufficientGemsModal.current} Gems
              </p>
            </div>
            <p style={{ color: '#DFBD69', marginBottom: '10px', fontSize: '14px' }}>
              Please purchase more Gems or select fewer time slots.
            </p>
            {/* <p style={{ color: '#a67c00', marginBottom: '20px', fontSize: '14px', fontWeight: 'bold' }}>
              Note: You can still proceed and pay by Card if you prefer.
            </p> */}
            <Button
              onClick={() => setInsufficientGemsModal({ show: false, required: 0, current: 0 })}
              theme="white"
              style={{ width: '100%', marginTop: '10px' }}
            >
              Close
            </Button>
          </div>
        </div>
      )}
      {/* Guardian Confirmation Modal */}
      {guardianModal && (
        <div style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, backgroundColor: "rgba(0,0,0,0.75)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 30000 }}
          onClick={() => setGuardianModal(false)}>
          <div style={{ backgroundColor: "#1a1a1a", borderRadius: 12, padding: "28px 24px", width: "min(460px, 92vw)", textAlign: "center", boxShadow: "0 8px 32px rgba(0,0,0,0.45)", border: "1px solid rgba(223,189,105,0.25)" }}
            onClick={(e) => e.stopPropagation()}>
            <h3 style={{ color: "#DFBD69", margin: "0 0 16px", fontSize: "1.2rem" }}>{t("cart.guardianConfirmationRequired")}</h3>
            <p style={{ color: "#fff2d2", marginBottom: 28, lineHeight: 1.6 }}>{t("cart.guardianMessage")}</p>
            <div style={{ display: "flex", gap: 12, justifyContent: "center" }}>
              <button style={{ background: "#DFBD69", color: "#151517", border: "none", borderRadius: 8, padding: "10px 22px", fontWeight: 700, cursor: "pointer", fontSize: "0.95rem" }}
                onClick={() => { setGuardianModal(false); setModalIsOpen(false); setIsModalOpen(true); }}>
                {t("cart.yesProceed")}
              </button>
              <button style={{ background: "transparent", color: "#DFBD69", border: "1px solid rgba(223,189,105,0.4)", borderRadius: 8, padding: "10px 22px", cursor: "pointer", fontSize: "0.95rem" }}
                onClick={() => setGuardianModal(false)}>
                {t("cart.noCancel")}
              </button>
            </div>
          </div>
        </div>
      )}
    </Container>
  );
}

export default ThirdPage;
